/* eslint-disable no-continue */
import { useContext, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client';
import { getIn, useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';
import { toast } from 'react-toastify';

import { Deal } from '../gql/dealGql';
import {
  DealProductsV2Query,
  PaymentOptionsV2Document,
  PaymentOptionsV2Query,
  PaymentOptionsV2QueryVariables,
  ProductSlug,
  useDealProductsV2Query,
  usePatchDealProductPricesV2Mutation,
} from '../gql/generated/graphql';

import { DealActionsEnum, DealContext } from '../libs/DealContext';

import useDebounce from './useDebounce';

const SLUG_FIELD_MAP: Record<string, { price: string; cost: string }> = {
  [ProductSlug.Vsc]: { price: 'financial_info.vsc_price', cost: 'financial_info.vsc_cost' },
  [ProductSlug.Gap]: { price: 'financial_info.gap_price', cost: 'financial_info.gap_cost' },
  [ProductSlug.TireWheel]: { price: 'tire_wheel_price', cost: 'tire_wheel_cost' },
  [ProductSlug.KeyReplacement]: {
    price: 'key_replacement_price',
    cost: 'key_replacement_cost',
  },
};

/**
 * Hook that syncs deal_products ↔ Formik fields and auto-saves product edits.
 *
 * - On load / refetch: deal_products → Formik (source of truth)
 * - On user edit (call `debouncedSave`): Formik → deal_products via patchDealProductPricesV2
 *
 * The SLUG_FIELD_MAP still uses the `financial_info.*` Formik field paths
 * (Approach A: kept as form-state field names), but those fields are no longer
 * fetched or sent to the server — they are purely deal_products-backed.
 */
export const useProductAutoSave = () => {
  const { deal, dispatch } = useContext(DealContext);
  const dealId = deal?.id ? Number(deal.id) : 0;
  const { values, setFieldValue } = useFormikContext<Deal>();
  const apolloClient = useApolloClient();

  const { data, refetch } = useDealProductsV2Query({
    variables: { dealId },
    skip: !dealId,
  });

  const [patchPrices, { loading: isSavingProductPrices }] = usePatchDealProductPricesV2Mutation();

  // Stabilize dealProducts reference — only update when data actually changes.
  // Prevents the sync effect from re-firing on every Apollo cache write.
  const dealProductsRef = useRef<DealProductsV2Query['dealProductsV2']>([]);
  const rawProducts = data?.dealProductsV2;

  useEffect(() => {
    if (rawProducts && !isEqual(rawProducts, dealProductsRef.current)) {
      dealProductsRef.current = rawProducts;
    }
  }, [rawProducts]);

  const dealProducts = dealProductsRef.current;

  // Sync deal_products → Formik after refetch (e.g., CalcButton).
  // On initial page load, initialValues in DealInfoBuyout.tsx is the source of truth.
  // Skipping the first sync prevents stale Apollo cache data from overwriting correct
  // initialValues (the deal query uses no-cache but this query uses cache-first).
  const hasInitialSynced = useRef(false);

  useEffect(() => {
    if (!dealProducts.length) {
      return;
    }

    if (!hasInitialSynced.current) {
      hasInitialSynced.current = true;
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const dp of dealProducts) {
      const fields = dp.product_catalog?.slug ? SLUG_FIELD_MAP[dp.product_catalog.slug] : null;
      if (!fields) {
        continue;
      }

      if (dp.price != null) {
        setFieldValue(fields.price, dp.price);
      }
      if (dp.cost != null) {
        setFieldValue(fields.cost, dp.cost);
      }
    }
  }, [dealProducts, setFieldValue]);

  // Keep a ref to latest values so the debounced callback never captures stale data.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Debounced save: reads latest Formik values + deal_products metadata
  const debouncedSave = useDebounce(() => {
    if (!dealId || !dealProducts.length) {
      return;
    }

    const products = dealProducts.map((dp) => {
      const slug = dp.product_catalog?.slug;
      const fields = slug ? SLUG_FIELD_MAP[slug] : null;
      const price = fields
        ? getIn(valuesRef.current, fields.price) ?? dp.price ?? 0
        : dp.price ?? 0;
      const cost = fields ? getIn(valuesRef.current, fields.cost) ?? dp.cost ?? 0 : dp.cost ?? 0;

      return {
        productCatalogId: dp.product_catalog_id,
        price,
        cost,
      };
    });

    const validProducts = products.filter((p) => p.price >= p.cost);
    const skippedProducts = products.filter((p) => p.price < p.cost);

    if (skippedProducts.length) {
      console.warn(
        '[useProductAutoSave] Skipped products with price < cost:',
        skippedProducts.map((p) => p.productCatalogId),
      );
    }

    if (!validProducts.length) {
      return;
    }

    patchPrices({
      variables: {
        input: { dealId, products: validProducts },
      },
    })
      .then(async () => {
        // Keep deal context's deal_products in sync so enableReinitialize
        // doesn't revert prices to stale values when the form reinitializes.
        const updatedDealProducts = (deal.deal_products ?? []).map((dp) => {
          const patched = validProducts.find((p) => p.productCatalogId === dp.product_catalog_id);
          if (!patched) {
            return dp;
          }
          return { ...dp, price: patched.price, cost: patched.cost };
        });
        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: { deal_products: updatedDealProducts },
        });

        // Sync Formik's derived financial_info money fields with the freshly
        // recomputed selected-slot payment so the next dealInfoUpsert auto-save
        // doesn't overwrite the API-persisted values with stale Formik state.
        const { data: paymentData } = await apolloClient.query<
          PaymentOptionsV2Query,
          PaymentOptionsV2QueryVariables
        >({
          query: PaymentOptionsV2Document,
          variables: { input: { dealId } },
          fetchPolicy: 'network-only',
        });
        const paymentOptions = paymentData?.paymentOptionsV2;
        const selectedSlot = paymentOptions?.selectedPackageSlotIndex;
        if (selectedSlot == null) {
          return;
        }
        const selectedPackage = paymentOptions.packages.find((p) => p.slotIndex === selectedSlot);
        if (!selectedPackage) {
          return;
        }
        setFieldValue('financial_info.payment', selectedPackage.sellPayment);
        setFieldValue('financial_info.amount_financed', selectedPackage.amountFinanced);
        setFieldValue('financial_info.reserve', selectedPackage.reserve);
        setFieldValue('financial_info.profit', selectedPackage.profit);
        setFieldValue('financial_info.setter_commission', selectedPackage.setterCommission);
        setFieldValue('financial_info.closer_commission', selectedPackage.closerCommission);
      })
      .catch(() => {
        toast.error('Failed to auto-save product prices', { toastId: 'product-auto-save-error' });
      });
  }, 2000);

  return { dealProducts, refetch, debouncedSave, isSavingProductPrices };
};
